import React, { useCallback, useEffect, useState } from 'react'
import Swal from 'sweetalert2'
import { supabase } from '../supabaseClient'
import { security } from '../config/security'
import 'bootstrap-icons/font/bootstrap-icons.css'
import './KitFornecedores.css'

const FORNECEDORES_PADRAO = ['Sou Energy', 'Fotus', 'Solfácil']
const POTENCIA_PADRAO = 620

const escapar = (valor) => String(valor ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;')

const parseNumero = (valor) => {
  if (valor == null) return null
  let texto = String(valor).trim()
  if (!texto) return null
  if (texto.includes(',')) texto = texto.replace(/\./g, '').replace(',', '.')
  const numero = Number(texto)
  return Number.isFinite(numero) ? numero : null
}

const formatarMoeda = (valor) => Number(valor).toLocaleString('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
})

const formatarKwh = (valor) => Number(valor).toLocaleString('pt-BR', { maximumFractionDigits: 2 })

const formatarKwp = (valor) => Number(valor).toLocaleString('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 2
})

const sugerirKwp = (kwh) => (kwh / 235).toFixed(2)

const KitFornecedores = ({ userEmpresa, userRole, onBack }) => {
  const empresaId = userEmpresa?.id || null
  const podeEditar = security.podeEditarKitFornecedores(userRole)
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState('')
  const [potencia, setPotencia] = useState(POTENCIA_PADRAO)
  const [fornecedores, setFornecedores] = useState([])
  const [faixas, setFaixas] = useState([])

  const carregar = useCallback(async () => {
    if (!empresaId) {
      setErro('Sua conta não está vinculada a uma empresa. O comparativo é salvo por empresa.')
      setLoading(false)
      return
    }

    setLoading(true)
    setErro('')
    try {
      const { data: config, error: erroConfig } = await supabase
        .from('kit_fornecedor_config')
        .select('potencia_painel_w')
        .eq('empresa_id', empresaId)
        .maybeSingle()
      if (erroConfig) throw erroConfig

      if (podeEditar && !config) {
        const { error: erroInsertConfig } = await supabase
          .from('kit_fornecedor_config')
          .insert({ empresa_id: empresaId, potencia_painel_w: POTENCIA_PADRAO })
        if (erroInsertConfig && erroInsertConfig.code !== '23505') throw erroInsertConfig

        if (!erroInsertConfig) {
          const { count, error: erroContagem } = await supabase
            .from('kit_fornecedores')
            .select('id', { count: 'exact', head: true })
            .eq('empresa_id', empresaId)
          if (erroContagem) throw erroContagem
          if (!count) {
            const { error: erroSeed } = await supabase.from('kit_fornecedores').insert(
              FORNECEDORES_PADRAO.map((nome, indice) => ({
                empresa_id: empresaId,
                nome,
                ordem: indice + 1
              }))
            )
            if (erroSeed && erroSeed.code !== '23505') throw erroSeed
          }
        }
      }

      const [{ data: listaFornecedores, error: erroFornecedores }, { data: listaFaixas, error: erroFaixas }] = await Promise.all([
        supabase.from('kit_fornecedores').select('id, nome, ordem').eq('empresa_id', empresaId).order('ordem', { ascending: true }).order('nome', { ascending: true }),
        supabase.from('kit_fornecedor_faixas').select('id, kwh, kwp').eq('empresa_id', empresaId).order('kwh', { ascending: true })
      ])
      if (erroFornecedores) throw erroFornecedores
      if (erroFaixas) throw erroFaixas

      const idsFaixas = (listaFaixas || []).map((faixa) => faixa.id)
      let precos = []
      if (idsFaixas.length) {
        const { data: listaPrecos, error: erroPrecos } = await supabase
          .from('kit_fornecedor_precos')
          .select('faixa_id, fornecedor_id, preco')
          .in('faixa_id', idsFaixas)
        if (erroPrecos) throw erroPrecos
        precos = listaPrecos || []
      }

      const precosPorFaixa = {}
      precos.forEach((item) => {
        if (!precosPorFaixa[item.faixa_id]) precosPorFaixa[item.faixa_id] = {}
        precosPorFaixa[item.faixa_id][item.fornecedor_id] = Number(item.preco)
      })

      const { data: configAtual } = await supabase
        .from('kit_fornecedor_config')
        .select('potencia_painel_w')
        .eq('empresa_id', empresaId)
        .maybeSingle()

      setPotencia(Number(configAtual?.potencia_painel_w) || POTENCIA_PADRAO)
      setFornecedores(listaFornecedores || [])
      setFaixas((listaFaixas || []).map((faixa) => ({
        ...faixa,
        kwh: Number(faixa.kwh),
        kwp: Number(faixa.kwp),
        precos: precosPorFaixa[faixa.id] || {}
      })))
    } catch (error) {
      console.error('Erro ao carregar Kit Fornecedores:', error)
      const faltandoTabela = /kit_fornecedor/i.test(error.message || '') && /schema cache|does not exist|não encontr/i.test(error.message || '')
      setErro(faltandoTabela
        ? 'As tabelas do comparativo ainda não existem no banco. Rode o script supabase/migrations/20261021120000_kit_fornecedores.sql no SQL Editor.'
        : `Não foi possível carregar o comparativo: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }, [empresaId, podeEditar])

  useEffect(() => {
    carregar()
  }, [carregar])

  const salvarPrecos = async (faixaId, precos) => {
    const { error: erroDelete } = await supabase
      .from('kit_fornecedor_precos')
      .delete()
      .eq('faixa_id', faixaId)
    if (erroDelete) throw erroDelete

    const linhas = Object.entries(precos)
      .filter(([, preco]) => preco > 0)
      .map(([fornecedorId, preco]) => ({
        faixa_id: faixaId,
        fornecedor_id: fornecedorId,
        preco
      }))
    if (!linhas.length) return
    const { error } = await supabase.from('kit_fornecedor_precos').insert(linhas)
    if (error) throw error
  }

  const exigirAdmin = () => {
    if (podeEditar) return true
    Swal.fire('Acesso restrito', 'Somente o administrador pode incluir ou editar.', 'info')
    return false
  }

  const alterarPotencia = async () => {
    if (!exigirAdmin()) return
    const resultado = await Swal.fire({
      title: 'Potência padrão do painel',
      text: 'Informe a potência em Watts (W) usada para calcular a quantidade de módulos.',
      input: 'number',
      inputValue: potencia,
      inputAttributes: { min: 100, max: 1000, step: 5 },
      showCancelButton: true,
      confirmButtonText: 'Salvar potência',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1e3a8a',
      inputValidator: (valor) => {
        const numero = parseInt(valor, 10)
        if (!numero || numero <= 0) return 'Informe um valor válido em Watts.'
        return undefined
      }
    })
    if (!resultado.isConfirmed) return

    const novaPotencia = parseInt(resultado.value, 10)
    const { error } = await supabase
      .from('kit_fornecedor_config')
      .upsert({
        empresa_id: empresaId,
        potencia_painel_w: novaPotencia,
        updated_at: new Date().toISOString()
      }, { onConflict: 'empresa_id' })
    if (error) {
      Swal.fire('Erro', error.message, 'error')
      return
    }
    setPotencia(novaPotencia)
    Swal.fire({
      icon: 'success',
      title: 'Atualizado',
      text: `Potência alterada para ${novaPotencia}W. As quantidades foram recalculadas.`,
      timer: 1800,
      showConfirmButton: false
    })
  }

  const abrirGerenciarFornecedores = (lista = fornecedores) => {
    if (!exigirAdmin()) return
    const itens = lista.length
      ? lista.map((fornecedor) => `
          <li class="list-group-item d-flex justify-content-between align-items-center py-2">
            <span class="fw-semibold"><i class="bi bi-building me-2 text-primary"></i>${escapar(fornecedor.nome)}</span>
            <span>
              <button type="button" class="btn btn-sm btn-outline-secondary border-0" data-rename="${fornecedor.id}" data-nome="${escapar(fornecedor.nome)}" title="Renomear">
                <i class="bi bi-pencil"></i>
              </button>
              <button type="button" class="btn btn-sm btn-outline-danger border-0" data-delete="${fornecedor.id}" title="Remover">
                <i class="bi bi-trash"></i>
              </button>
            </span>
          </li>
        `).join('')
      : '<li class="list-group-item text-muted text-center">Nenhum fornecedor cadastrado.</li>'

    Swal.fire({
      title: 'Gerenciar fornecedores',
      html: `
        <div class="text-start mb-3">
          <label class="form-label fw-semibold" for="novoFornecedor">Cadastrar novo fornecedor</label>
          <div class="input-group">
            <input type="text" id="novoFornecedor" class="form-control" placeholder="Ex: Aldo Solar, Edeltec...">
            <button type="button" class="btn btn-primary" id="btnNovoFornecedor">Adicionar</button>
          </div>
          <div id="erroFornecedor" class="text-danger small mt-2"></div>
        </div>
        <hr>
        <div class="text-start">
          <label class="form-label fw-semibold">Fornecedores ativos</label>
          <ul class="list-group" style="max-height: 240px; overflow-y: auto;">${itens}</ul>
        </div>
      `,
      showCloseButton: true,
      showConfirmButton: false,
      didOpen: () => {
        const input = document.getElementById('novoFornecedor')
        const botao = document.getElementById('btnNovoFornecedor')
        const aviso = document.getElementById('erroFornecedor')
        const adicionar = async () => {
          const nome = input?.value.trim()
          if (!nome) {
            aviso.textContent = 'Digite o nome do fornecedor.'
            return
          }
          if (lista.some((item) => item.nome.toLowerCase() === nome.toLowerCase())) {
            aviso.textContent = 'Este fornecedor já está cadastrado.'
            return
          }
          botao.disabled = true
          const { error } = await supabase.from('kit_fornecedores').insert({
            empresa_id: empresaId,
            nome,
            ordem: lista.length + 1
          })
          if (error) {
            aviso.textContent = error.code === '23505' ? 'Este fornecedor já está cadastrado.' : error.message
            botao.disabled = false
            return
          }
          const atualizados = await recarregarLista()
          abrirGerenciarFornecedores(atualizados)
        }
        botao?.addEventListener('click', adicionar)
        input?.addEventListener('keydown', (evento) => {
          if (evento.key === 'Enter') {
            evento.preventDefault()
            adicionar()
          }
        })
        document.querySelectorAll('[data-rename]').forEach((botaoRenomear) => {
          botaoRenomear.addEventListener('click', () => renomearFornecedor(botaoRenomear.dataset.rename, botaoRenomear.dataset.nome))
        })
        document.querySelectorAll('[data-delete]').forEach((botaoExcluir) => {
          botaoExcluir.addEventListener('click', () => excluirFornecedor(botaoExcluir.dataset.delete))
        })
      }
    })
  }

  const recarregarLista = async () => {
    const { data, error } = await supabase
      .from('kit_fornecedores')
      .select('id, nome, ordem')
      .eq('empresa_id', empresaId)
      .order('ordem', { ascending: true })
      .order('nome', { ascending: true })
    if (error) throw error
    setFornecedores(data || [])
    return data || []
  }

  const renomearFornecedor = async (id, nomeAtual) => {
    if (!exigirAdmin()) return
    const resultado = await Swal.fire({
      title: 'Renomear fornecedor',
      input: 'text',
      inputValue: nomeAtual,
      showCancelButton: true,
      confirmButtonText: 'Salvar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1e3a8a',
      inputValidator: (valor) => (!valor || !valor.trim() ? 'Informe o nome.' : undefined)
    })
    if (!resultado.isConfirmed) {
      abrirGerenciarFornecedores()
      return
    }
    const nome = resultado.value.trim()
    const { error } = await supabase.from('kit_fornecedores').update({ nome }).eq('id', id)
    if (error) {
      Swal.fire('Erro', error.code === '23505' ? 'Já existe um fornecedor com este nome.' : error.message, 'error')
      return
    }
    const atualizados = await recarregarLista()
    abrirGerenciarFornecedores(atualizados)
  }

  const excluirFornecedor = async (id) => {
    if (!exigirAdmin()) return
    const resultado = await Swal.fire({
      title: 'Remover fornecedor?',
      text: 'A coluna e os preços deste fornecedor saem de todas as faixas.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#1e3a8a',
      confirmButtonText: 'Sim, remover',
      cancelButtonText: 'Cancelar'
    })
    if (!resultado.isConfirmed) {
      abrirGerenciarFornecedores()
      return
    }
    const { error } = await supabase.from('kit_fornecedores').delete().eq('id', id)
    if (error) {
      Swal.fire('Erro', error.message, 'error')
      return
    }
    await carregar()
    const { data } = await supabase
      .from('kit_fornecedores')
      .select('id, nome, ordem')
      .eq('empresa_id', empresaId)
      .order('ordem', { ascending: true })
    abrirGerenciarFornecedores(data || [])
  }

  const htmlFormularioFaixa = (faixa) => {
    const camposPreco = fornecedores.map((fornecedor) => {
      const atual = faixa?.precos?.[fornecedor.id]
      return `
        <div class="mb-3 text-start">
          <label class="form-label fw-semibold" for="preco_${fornecedor.id}">Preço ${escapar(fornecedor.nome)} (R$)</label>
          <input type="text" inputmode="decimal" id="preco_${fornecedor.id}" class="form-control" placeholder="0,00" value="${atual ? formatarMoeda(atual) : ''}">
        </div>
      `
    }).join('')

    return `
      <div class="row g-2 mb-3 text-start">
        <div class="col-6">
          <label class="form-label fw-semibold" for="kitKwh">Consumo (kWh/mês)</label>
          <input type="text" inputmode="decimal" id="kitKwh" class="form-control" placeholder="Ex: 800" value="${faixa ? faixa.kwh : ''}">
        </div>
        <div class="col-6">
          <label class="form-label fw-semibold" for="kitKwp">Potência (kWp)</label>
          <input type="text" inputmode="decimal" id="kitKwp" class="form-control" placeholder="Ex: 3,4" value="${faixa ? String(faixa.kwp).replace('.', ',') : ''}">
        </div>
      </div>
      <div class="border-top pt-3">
        <h6 class="fw-bold text-start mb-3 text-secondary">Preços por fornecedor</h6>
        ${camposPreco || '<p class="text-muted text-start">Cadastre um fornecedor antes de informar preços.</p>'}
      </div>
    `
  }

  const lerFormularioFaixa = () => {
    const kwh = parseNumero(document.getElementById('kitKwh')?.value)
    const kwp = parseNumero(document.getElementById('kitKwp')?.value)
    if (!kwh || kwh <= 0 || !kwp || kwp <= 0) {
      Swal.showValidationMessage('Preencha kWh e kWp com valores maiores que zero.')
      return false
    }
    const precos = {}
    fornecedores.forEach((fornecedor) => {
      const valor = parseNumero(document.getElementById(`preco_${fornecedor.id}`)?.value)
      if (valor && valor > 0) precos[fornecedor.id] = valor
    })
    return { kwh, kwp, precos }
  }

  const ligarSugestaoKwp = () => {
    const kwhInput = document.getElementById('kitKwh')
    const kwpInput = document.getElementById('kitKwp')
    kwhInput?.addEventListener('input', () => {
      const kwh = parseNumero(kwhInput.value)
      if (kwh > 0 && kwpInput && document.activeElement === kwhInput) {
        kwpInput.value = String(sugerirKwp(kwh)).replace('.', ',')
      }
    })
  }

  const abrirNovaFaixa = async () => {
    if (!exigirAdmin()) return
    if (!fornecedores.length) {
      Swal.fire('Atenção', 'Cadastre pelo menos um fornecedor antes de criar uma faixa.', 'warning')
      return
    }
    const resultado = await Swal.fire({
      title: 'Nova faixa / kit solar',
      html: htmlFormularioFaixa(null),
      width: 640,
      showCancelButton: true,
      confirmButtonText: 'Salvar proposta',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1e3a8a',
      didOpen: ligarSugestaoKwp,
      preConfirm: lerFormularioFaixa
    })
    if (!resultado.isConfirmed || !resultado.value) return

    const { data, error } = await supabase
      .from('kit_fornecedor_faixas')
      .insert({
        empresa_id: empresaId,
        kwh: resultado.value.kwh,
        kwp: resultado.value.kwp
      })
      .select('id')
      .single()
    if (error) {
      Swal.fire('Erro', error.message, 'error')
      return
    }
    try {
      await salvarPrecos(data.id, resultado.value.precos)
    } catch (errorSalvar) {
      Swal.fire('Erro', errorSalvar.message, 'error')
      return
    }
    await carregar()
    Swal.fire({ icon: 'success', title: 'Faixa adicionada', timer: 1400, showConfirmButton: false })
  }

  const abrirEditarFaixa = async (faixa) => {
    if (!exigirAdmin()) return
    const resultado = await Swal.fire({
      title: 'Editar faixa / kit',
      html: htmlFormularioFaixa(faixa),
      width: 640,
      showCancelButton: true,
      confirmButtonText: 'Atualizar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1e3a8a',
      preConfirm: lerFormularioFaixa
    })
    if (!resultado.isConfirmed || !resultado.value) return

    const { error } = await supabase
      .from('kit_fornecedor_faixas')
      .update({ kwh: resultado.value.kwh, kwp: resultado.value.kwp })
      .eq('id', faixa.id)
    if (error) {
      Swal.fire('Erro', error.message, 'error')
      return
    }
    try {
      await salvarPrecos(faixa.id, resultado.value.precos)
    } catch (errorSalvar) {
      Swal.fire('Erro', errorSalvar.message, 'error')
      return
    }
    await carregar()
    Swal.fire({ icon: 'success', title: 'Faixa atualizada', timer: 1400, showConfirmButton: false })
  }

  const excluirFaixa = async (faixa) => {
    if (!exigirAdmin()) return
    const resultado = await Swal.fire({
      title: 'Excluir esta faixa?',
      text: `O consumo de ${formatarKwh(faixa.kwh)} kWh/mês será removido do comparativo.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#1e3a8a',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar'
    })
    if (!resultado.isConfirmed) return
    const { error } = await supabase.from('kit_fornecedor_faixas').delete().eq('id', faixa.id)
    if (error) {
      Swal.fire('Erro', error.message, 'error')
      return
    }
    await carregar()
    Swal.fire({ icon: 'success', title: 'Excluída', timer: 1200, showConfirmButton: false })
  }

  const menorPreco = (faixa) => {
    const valores = fornecedores
      .map((fornecedor) => faixa.precos[fornecedor.id])
      .filter((preco) => preco > 0)
    if (!valores.length) return null
    return Math.min(...valores)
  }

  return (
    <div className="kf-page">
      <button type="button" className="kf-back" onClick={onBack}>← Voltar</button>

      <div className="row g-3 mb-1">
        <div className="col-12 col-md-5 col-lg-4">
          <div className="kf-card kf-config">
            <div>
              <small>Potência do painel padrão</small>
              <h2>{potencia} Watts</h2>
            </div>
            {podeEditar && (
              <button type="button" className="kf-icon-btn" onClick={alterarPotencia} title="Alterar watts do painel" disabled={!empresaId || !!erro}>
                <i className="bi bi-pencil-square" />
              </button>
            )}
          </div>
        </div>
        {podeEditar && (
        <div className="col-12 col-md-7 col-lg-8">
          <div className="kf-actions">
            <button type="button" className="kf-btn-outline" onClick={() => abrirGerenciarFornecedores()} disabled={!empresaId || !!erro}>
              <i className="bi bi-truck me-2" />Gerenciar fornecedores
            </button>
            <button type="button" className="kf-btn-solar" onClick={abrirNovaFaixa} disabled={!empresaId || !!erro}>
              <i className="bi bi-plus-circle me-2" />Nova proposta / kit
            </button>
          </div>
        </div>
        )}
      </div>

      <div className="kf-tip">
        <i className="bi bi-info-circle fs-5" />
        <div>
          {podeEditar ? (
            <>
              <strong>Dica de cálculo:</strong> o kWp sugerido usa o consumo dividido por 235 e pode ser alterado.
              A quantidade de módulos usa painéis de <strong>{potencia}W</strong>. O menor preço de cada faixa fica destacado.
            </>
          ) : (
            <>
              Consulta do comparativo. Incluir e editar fica disponível somente para o administrador.
              A quantidade de módulos usa painéis de <strong>{potencia}W</strong>.
            </>
          )}
        </div>
      </div>

      {erro && <div className="kf-error">{erro}</div>}

      <div className="kf-card">
        <div className="kf-head">
          <h3><i className="bi bi-table me-2" />Tabela comparativa de kits</h3>
          <span className="kf-badge">{faixas.length} {faixas.length === 1 ? 'faixa cadastrada' : 'faixas cadastradas'}</span>
        </div>
        <div className="kf-table-wrap">
          <table className="table kf-table">
            <thead>
              <tr>
                <th style={{ minWidth: 150 }}>Consumo (kWh)</th>
                <th style={{ minWidth: 130 }}>Potência (kWp)</th>
                <th style={{ minWidth: 140 }}>Qtd. módulos</th>
                {fornecedores.map((fornecedor) => (
                  <th key={fornecedor.id} style={{ minWidth: 160 }}>{fornecedor.nome}</th>
                ))}
                {podeEditar && <th style={{ minWidth: 110 }}>Ações</th>}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={3 + fornecedores.length + (podeEditar ? 1 : 0)} className="kf-empty">Carregando comparativo...</td>
                </tr>
              )}
              {!loading && faixas.length === 0 && (
                <tr>
                  <td colSpan={3 + fornecedores.length + (podeEditar ? 1 : 0)} className="kf-empty">
                    <i className="bi bi-box-seam d-block mb-2 fs-3" />
                    {podeEditar
                      ? <>Nenhuma faixa cadastrada. Clique em <strong>Nova proposta / kit</strong> para começar.</>
                      : 'Nenhuma faixa cadastrada.'}
                  </td>
                </tr>
              )}
              {!loading && faixas.map((faixa) => {
                const modulos = Math.ceil((faixa.kwp * 1000) / potencia)
                const minimo = menorPreco(faixa)
                return (
                  <tr key={faixa.id}>
                    <td className="fw-bold text-primary">{formatarKwh(faixa.kwh)} kWh/mês</td>
                    <td className="fw-semibold">{formatarKwp(faixa.kwp)} kWp</td>
                    <td>
                      <span className="kf-modules">
                        <i className="bi bi-sun me-1" />{modulos} x {potencia}W
                      </span>
                    </td>
                    {fornecedores.map((fornecedor) => {
                      const preco = faixa.precos[fornecedor.id]
                      const melhor = preco > 0 && preco === minimo
                      if (!(preco > 0)) {
                        return <td key={fornecedor.id} className="text-muted fst-italic">N/A</td>
                      }
                      return (
                        <td key={fornecedor.id} className={`kf-price ${melhor ? 'kf-min' : ''}`}>
                          {melhor && <div className="kf-best">Melhor preço</div>}
                          R$ {formatarMoeda(preco)}
                        </td>
                      )
                    })}
                    {podeEditar && (
                    <td>
                      <div className="btn-group btn-group-sm">
                        <button type="button" className="btn btn-outline-primary" title="Editar" onClick={() => abrirEditarFaixa(faixa)}>
                          <i className="bi bi-pencil" />
                        </button>
                        <button type="button" className="btn btn-outline-danger" title="Excluir" onClick={() => excluirFaixa(faixa)}>
                          <i className="bi bi-trash" />
                        </button>
                      </div>
                    </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default KitFornecedores
